/* eslint-disable arrow-parens */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormControl, Button, InputAdornment, IconButton } from "@material-ui/core";
import { trackPromise } from "react-promise-tracker";

// other-shared-components / files
import { ThemeCustomInputField } from "../../../shared/themeComponents/themeCustomInputField";
import { ADMIN_ENV, MEMBER_PORTAL_REDIRECTION } from "../../../../config/config";
import { api } from "../../../../utils/api";
import ROLES from "../../../../constants/roles";
import SelectMenuItems from "../../../shared/selectMenuItems/SelectMenuItems";
import SignUpFooterLink from "../authFooter/SignUpFooterLink";
import ForgetCredentialFooterLink from "../authFooter/ForgetCredentialFooterLink";
import CopyrightFooterLink from "../authFooter/CopyrightFooterLink";
import { Validation } from "../../../../@types/validation.types";

// images & icons
import { nexcaliberLogo } from "../../../../assets";
import { loginPageImage } from "../../../../assets";
import AccountCircle from "@material-ui/icons/AccountCircle";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PersonIcon from "@material-ui/icons/Person";

import "./login.scss";

type LoginProps = {
	setLoginState: any;
};

const Login: React.FC<LoginProps> = ({ setLoginState }) => {
	const [credential, setCredential] = useState({
		user_id: "",
		role: "",
		password: ""
	});
	const [showPassword, setShowPassword] = useState(false);
	const [anchorElement, setAnchorElement] = useState<(EventTarget & HTMLButtonElement) | null>(null);
	const [loginDialogProps, setLoginDialogProps] = useState();
	const [validation, setValidation] = useState<Validation>({
		employee: {},
		reEnterSSN: "",
		paycheck: {},
		status: "invalid"
	});
	const _validation = useRef<any>();
	const navigate = useNavigate();
	const roles = Object.values(ROLES);
	const [rememberMe, setRememberMe] = useState(true);

	const handleValidation = useCallback(async () => {
		const { role, user_id, password } = credential;

		_validation.current = {
			...validation,
			credential: {},
			status: "invalid"
		};
		let flag = true;

		if (role.length === 0) {
			_validation.current["role"] = "Role is required";
			_validation.current["status"] = "invalid";
			flag = false;
		} else {
			_validation.current["role"] = "";
		}
		if (user_id.length === 0) {
			_validation.current["user_id"] = "User name/Email is required";
			_validation.current["status"] = "invalid";
			flag = false;
		} else {
			_validation.current["user_id"] = "";
		}
		if (password.length === 0) {
			_validation.current["password"] = "Password is required";
			_validation.current["status"] = "invalid";
			flag = false;
		} else {
			_validation.current["password"] = "";
		}

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [credential, validation]);

	const handleShowPassword = useCallback(() => {
		setShowPassword(!showPassword);
	}, [showPassword]);

	const handleCredentialChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;
			setCredential(Object.assign({}, credential, { [name]: value }));
		},
		[credential]
	);

	const handleCredentialRoleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, name: string) => {
			setAnchorElement(null);
			const value = event.currentTarget.getAttribute("value");

			setCredential(Object.assign({}, credential, { [name]: value }));
		},
		[credential]
	);

	const handleRoleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		const { currentTarget } = event;
		setAnchorElement(currentTarget);
	};

	const handleRoleMenuClose = () => {
		setAnchorElement(null);
	};

	const handleAuthenticationRedirection = useCallback(
		async (role: string, token: string, group_number: number, response: any) => {
			switch (role) {
				case ROLES.super_admin:
				case ROLES.admin:
					localStorage.setItem("@jwt", token);
					navigate("/");
					break;
				case ROLES.employer:
					localStorage.setItem("@jwt", token);
					navigate(`/employer?gn=${group_number}`);
					break;
				case ROLES.employee:
				case ROLES.dependent:
					window.open(
						`${MEMBER_PORTAL_REDIRECTION}/verify?token=${token}&id=${response?.result._id}&is_emp=1`
					);
					break;
				default:
					alert("Invalid role selected!");
			}
		},
		[navigate]
	);

	const handleLoginButtonClick = useCallback(
		async (event: React.FormEvent<HTMLFormElement> | undefined) => {
			event?.preventDefault();

			const status = await handleValidation();

			if (status === "valid") {
				try {
					const _credential = {
						...credential,
						role: credential.role.toUpperCase()
					};

					const response = await trackPromise(api.auth.login(_credential));
					console.log("Login Data", response);

					if (response?.message === "Authentication Successful!") {
						const {
							result: { role, group_number },
							token
						} = response;
						setLoginState(true);
						//ad the remember me function
						handleRememberme();
						await handleAuthenticationRedirection(role, token, group_number, response);
					} else if (response)
						setLoginDialogProps(
							Object.assign({}, loginDialogProps, {
								openDialog: true,
								title: "Login failed!",
								content: "Invalid Role or Username or Password",
								actions: [
									{
										label: "Close",
										callback: () =>
											setLoginDialogProps(
												Object.assign({}, loginDialogProps, {
													openDialog: false
												})
											)
									}
								]
							})
						);
				} catch (err) {
					console.log("err", err);
					setLoginDialogProps(
						Object.assign({}, loginDialogProps, {
							openDialog: true,
							title: "Invalid Role or Username or Password",
							actions: [
								{
									label: "Close",
									callback: () =>
										setLoginDialogProps(
											Object.assign({}, loginDialogProps, {
												openDialog: false
											})
										)
								}
							]
						})
					);
				}
			} else {
				console.log("Form is invalid");
			}
		},
		[handleValidation, credential, loginDialogProps, handleAuthenticationRedirection]
	);
	//handeling the remember me button
	const handleRememberme = () => {
		if (rememberMe === true) {
			localStorage.setItem("role", role);
			localStorage.setItem("User_id", user_id);
		} else {
			localStorage.removeItem("role");
			localStorage.removeItem("User_id");
		}
	};

	const { role, user_id, password } = credential;

	// console.log("responsedas--->>>>>>>>>>>>>>>>", rememberMe);
	useEffect(() => {
		const userId = localStorage.getItem("User_id");
		const role = localStorage.getItem("role");

		if (userId || role) {
			setCredential((prev) => ({
				...prev,
				user_id: userId ?? "",
				role: role ?? ""
			}));
		}
	}, []);

	return (
		<div className="login-main-container" id="main-container">
			<div className="left-section">
				<img className="login-page-illustration" src={loginPageImage} alt="" />
				<div className="tagline">
					<h3>Self Reliance</h3>
					<span>is your Best Armour</span>
				</div>
			</div>
			<div className="right-section">
				{ADMIN_ENV === "DEV" ? (
					<div className="test-portal-login-container" style={{ marginTop: "-40px" }}>
						<h2 className="test-portal-login-text">Test Portal</h2>
					</div>
				) : null}
				<img className="nexcaliber-logo" src={nexcaliberLogo} alt="" />
				<h3 className="welcome-text">
					Welcome <span className="back-text">Back</span>
				</h3>
				<span className="subheader">Login with your credentials, to access the portal</span>
				<form onSubmit={handleLoginButtonClick} autoComplete="off" method="">
					<FormControl className="form-container" id="form-container">
						<div className="select-role-container" id="select-role-container">
							<Button
								className="role-select-button"
								onClick={handleRoleClick}
								endIcon={<ExpandMoreIcon className="expand-icon" />}
							>
								{role !== "" ? role : "Select Your Role"}
							</Button>
							<SelectMenuItems
								fieldName="role"
								anchorElement={anchorElement}
								listItems={roles}
								handleMenuClose={handleRoleMenuClose}
								handleSelecMenuItemClick={handleCredentialRoleChange}
							/>
							{validation?.role ? (
								<div className="details validation-text">
									<span className="select-validation-text">{validation?.role}</span>
								</div>
							) : null}
						</div>
						<ThemeCustomInputField
							className="form-field-input"
							label="Enter your username / email"
							name="user_id"
							variant="outlined"
							value={user_id}
							onChange={handleCredentialChange}
							helperText={validation?.user_id ? validation.user_id : null}
							InputProps={{
								endAdornment: (
									<InputAdornment position="start">
										<AccountCircle className="adornment-color" />
									</InputAdornment>
								)
							}}
						/>
						<ThemeCustomInputField
							className="form-field-input"
							type={!showPassword ? "password" : "text"}
							name="password"
							label="Enter your password"
							variant="outlined"
							value={password}
							onChange={handleCredentialChange}
							helperText={validation?.password ? validation?.password : null}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{!showPassword ? (
											<IconButton onClick={handleShowPassword}>
												<VisibilityOffIcon className="adornment-color" />
											</IconButton>
										) : (
											<IconButton onClick={handleShowPassword}>
												<VisibilityIcon className="adornment-color" />
											</IconButton>
										)}
									</InputAdornment>
								)
							}}
						/>
						<div className="rememberme-checkbox-container">
							<span className="remember-me-button" id="remember-me-button">
								<input
									type="checkbox"
									checked={rememberMe}
									onChange={(e) => setRememberMe(e.target.checked)}
								/>
								Remember Me
							</span>
						</div>

						<SignUpFooterLink />
						<ForgetCredentialFooterLink />
						<div className="login-button-container" id="login-button-container">
							<Button className="login-button" variant="contained" type="submit">
								<span className="button-label-with-icon" style={{ color: "#ffff" }}>
									Login
								</span>
								<span>
									<PersonIcon className="button-icon" style={{ color: "#ffff" }} />
								</span>
							</Button>
						</div>
					</FormControl>
				</form>
				<CopyrightFooterLink styleClass="login-page-copyright-text-container" />
			</div>
		</div>
	);
};

export default Login;
